import {useEffect, useState} from "react"
import {Loader2, RotateCw, Trash2, TriangleAlert} from "lucide-react"

import {Button} from "@/components/ui/button"
import {type DocumentRow, formatKyivTime, isRetryWindowOpen,} from "@/lib/documents"
import {useReadOnly, useRolePermissions} from "@/store/session"

const READ_ONLY_REASON = "Суб'єкта ОТК виключено з реєстру; доступний лише перегляд."
const NO_PERMISSION_REASON = "Потрібне право «Підпис і надсилання до ЄДРТЗ»."
// `DELETE /otk/documents/:id` is `deleteDraftDocument` under `manageDrafts`
// and refuses anything but a `DRAFT`. Deleting a signed-but-untransferred
// document needs its own backend operation — it has to release the linked
// electronic unit back to stock and either release or annul the physical
// blank depending on whether it was printed, which no endpoint does today.
// documents.md records both as presumed (BACKEND-HANDOFF.md § 5.1). The
// documented action stays visible and disabled rather than wired to a call
// that would fail, or faked client-side against the warehouse store.
const DELETE_UNAVAILABLE_REASON =
  "Видалення невідправленого документа ще не підтримане бекендом."

function retryNotReadyReason(nextRetryAtISO: string): string {
  return `Повторна відправка буде доступна о ${formatKyivTime(nextRetryAtISO)}.`
}

/**
 * `POST /otk/documents/:id/retry` answers `425 SUBMISSION_NOT_READY` while the
 * row's `nextRetryAt` has not elapsed. The button is already disabled for
 * that window, so a 425 only gets here when the client clock runs ahead of
 * the server's.
 */
function retryErrorMessage(err: unknown): string {
  const response = (err as {
    response?: { status?: number; data?: { document?: { submission?: { nextRetryAt?: string } } } }
  })?.response
  if (response?.status === 425) {
    const nextRetryAt = response.data?.document?.submission?.nextRetryAt
    return nextRetryAt
      ? retryNotReadyReason(nextRetryAt)
      : "Відправлення ще триває — повторіть спробу за кілька секунд."
  }
  if (response?.status === 403) return NO_PERMISSION_REASON
  return "Не вдалося повторити відправку. Спробуйте ще раз."
}

/**
 * Re-renders once `nextRetryAtISO` passes, so a row whose retry window
 * elapses while the list is open enables its button without a refetch.
 */
function useRetryWindowOpen(row: DocumentRow): boolean {
  const [nowMs, setNowMs] = useState(() => Date.now())
  const open = isRetryWindowOpen(row, nowMs)
  useEffect(() => {
    if (open || row.nextRetryAtISO === null) return
    const delay = Date.parse(row.nextRetryAtISO) - Date.now()
    const timer = setTimeout(() => setNowMs(Date.now()), Math.max(delay, 0) + 50)
    return () => clearTimeout(timer)
  }, [open, row.nextRetryAtISO])
  return open
}

/**
 * documents.md's `UnsentRowActions` — the only two actions a
 * `НЕ ВІДПРАВЛЕНО` row offers. No «Редагувати», «Дублювати» or «Анулювати»:
 * the document is already signed.
 *
 * Labels collapse to an accessible name below `lg`, where the actions column
 * is 96px wide (see `DataTable`'s own actions `TableHead`).
 */
export function UnsentRowActions({
  document,
  onRetrySend,
}: {
  document: DocumentRow
  /** Resolves once the retry's own refetch has settled. */
  onRetrySend: (id: string) => Promise<void>
}) {
  const readOnly = useReadOnly()
  const { signAndSendEdrtz } = useRolePermissions()
  const [retrying, setRetrying] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)
  const retryWindowOpen = useRetryWindowOpen(document)

  const blockedReason = readOnly
    ? READ_ONLY_REASON
    : !signAndSendEdrtz
      ? NO_PERMISSION_REASON
      : undefined
  const retryBlockedReason =
    blockedReason ??
    (retryWindowOpen || document.nextRetryAtISO === null
      ? undefined
      : retryNotReadyReason(document.nextRetryAtISO))

  async function handleRetry() {
    setRetrying(true)
    setRetryError(null)
    try {
      await onRetrySend(document.id)
    } catch (err) {
      console.error(err)
      setRetryError(retryErrorMessage(err))
    } finally {
      // On success the row is usually gone (moved to «Відправлено») by the
      // time this runs, so the update lands on an unmounted component —
      // harmless in React 18+, and a mounted-ref guard buys nothing for a
      // button that disappears with its row.
      setRetrying(false)
    }
  }

  return (
    <>
      <Button
        disabled={retrying || retryBlockedReason !== undefined}
        onClick={() => void handleRetry()}
        size="sm"
        title={
          [...new Set([retryError, retryBlockedReason].filter(Boolean))].join(" ") ||
          undefined
        }
        variant={retryError ? "outline" : "default"}
      >
        {retrying ? (
          <Loader2 className="animate-spin" data-icon="inline-start" />
        ) : retryError ? (
          <TriangleAlert className="text-destructive" data-icon="inline-start" />
        ) : (
          <RotateCw data-icon="inline-start" />
        )}
        <span className="max-lg:sr-only">
          {retrying ? "Відправлення…" : "Повторити Відправку"}
        </span>
      </Button>
      {/*
        The failure has nowhere else to go on this page — documents.md puts
        it in a toast, and this package has no toast layer yet. Announced
        here so it is not silent, with the text itself on the button's own
        `title`.
      */}
      <span aria-live="polite" className="sr-only">
        {retryError}
      </span>
      <Button
        disabled
        size="sm"
        title={blockedReason ?? DELETE_UNAVAILABLE_REASON}
        variant="destructive"
      >
        <Trash2 data-icon="inline-start" />
        <span className="max-lg:sr-only">Видалити</span>
      </Button>
    </>
  )
}
